import { useAuth0 } from 'auth0/reactAuth0Spa';
import { checkLoginForError, dateFormatToDBFormat, formatBearerToken } from 'Common.functions';
import { DepartmentContext } from 'DepartmentWrapper';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { fetchDataAgnostic, postDataAgnostic, putData } from 'Services/dataApi';
import style from './DirectApplicationSettings.style';
import { Button, Card, CardBody, CardHeader, FormGroup, Input, Label, Table } from 'reactstrap';
import { DatePicker } from 'react-widgets/cjs';
import { toast } from 'react-toastify';

import RezRateBanner from 'Body/RezRATE/Common/RezRateBanner/RezRateBanner';
import dayjs from 'dayjs';
import GenerateApplicationURLCode from 'Body/RezRATE/Body/DirectApplication/GenerateApplicationURLCode/GenerateApplicationURLCode';

const DOMAIN_LABELS = {
  patient_care: 'Patient Care',
  medical_knowledge: 'Medical Knowledge',
  technical_skill: 'Technical Skill',
  research: 'Research',
  initiative: 'Initiative',
  professionalism_communication: 'Professionalism & Communication',
  coachability: 'Coachability',
  overall_rank: 'Overall Rank',
};

const DEFAULT_TEMPLATE_POLICY = {
  RequireStandardizedTemplate: false,
  AllowedTemplateIds: [],
};

const DEFAULT_LOR_SETTINGS = {
  LoRMinRequired: 3,
  LoRMaxAccepted: 4,
  LoRRequiredForReview: false,
  LoRTemplatePolicyJson: DEFAULT_TEMPLATE_POLICY,
};

const defaultMappingsByRankType = {
  evaluation: [],
  prescreen: [],
};

const POLICY_SCOPE_OPTIONS = ['season_default', 'specialty', 'track'];
const POLICY_SCOPE_LABELS = {
  season_default: 'Season Default',
  specialty: 'Specialty',
  track: 'Track',
};

const REQUIREMENT_TYPE_OPTIONS = ['pdf', 'template'];
const REQUIREMENT_TYPE_LABELS = {
  pdf: 'PDF Letter',
  template: 'Standardized Template',
};
const showAdvancedDirectApplicationSettings = false;

const toBool = (value) => {
  if (value === true || value === 1 || value === '1') {
    return true;
  }
  if (value === false || value === 0 || value === '0') {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return false;
};

const coerceNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return parsed;
};

const normalizeTemplatePolicy = (rawPolicy) => {
  if (!rawPolicy || typeof rawPolicy !== 'object') {
    return { ...DEFAULT_TEMPLATE_POLICY };
  }

  const allowedTemplateIds = Array.isArray(rawPolicy.AllowedTemplateIds)
    ? rawPolicy.AllowedTemplateIds.map((templateId) => Number(templateId)).filter(
        (templateId) => !Number.isNaN(templateId),
      )
    : [];

  return {
    RequireStandardizedTemplate: toBool(rawPolicy.RequireStandardizedTemplate),
    AllowedTemplateIds: allowedTemplateIds,
  };
};

const normalizeLoRSettings = (rawSettings = {}) => {
  const templatePolicy = normalizeTemplatePolicy(rawSettings.LoRTemplatePolicyJson);

  return {
    LoRMinRequired: coerceNumber(rawSettings.LoRMinRequired, DEFAULT_LOR_SETTINGS.LoRMinRequired),
    LoRMaxAccepted: coerceNumber(rawSettings.LoRMaxAccepted, DEFAULT_LOR_SETTINGS.LoRMaxAccepted),
    LoRRequiredForReview: toBool(rawSettings.LoRRequiredForReview),
    LoRTemplatePolicyJson: templatePolicy,
  };
};

const createClientId = () =>
  `map_${Date.now()}_${Math.random()
    .toString(16)
    .slice(2)}`;

const normalizeMappings = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => ({
    ClientId: createClientId(),
    pk_LoRScoreMapping: row.pk_LoRScoreMapping,
    SourceDomainKey: row.SourceDomainKey || 'overall_rank',
    TargetWeightCategory: coerceNumber(row.TargetWeightCategory, null),
    ContributionType: row.ContributionType || 'normalized',
    Multiplier: coerceNumber(row.Multiplier, 1),
    Enabled: toBool(row.Enabled === undefined ? true : row.Enabled),
  }));
};

const extractApiErrorMessages = (error, fallbackMessage) => {
  const details = error?.response?.data?.details;
  if (Array.isArray(details) && details.length > 0) {
    const normalizedDetails = details
      .filter((item) => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => !!item);
    if (normalizedDetails.length > 0) {
      return normalizedDetails;
    }
  }

  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return [message.trim()];
  }

  return [fallbackMessage];
};

const normalizeDepartmentProgramSpecialties = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => ({
    ClientId: createClientId(),
    pk_DepartmentProgramSpecialty: coerceNumber(row.pk_DepartmentProgramSpecialty, null),
    fk_ProgramSpecialty: coerceNumber(row.fk_ProgramSpecialty, null),
    DisplayName: row.DisplayName || '',
    SpecialtyKey: row.SpecialtyKey || '',
    Level: row.Level || '',
    TrainingType: row.TrainingType || '',
    IsDefault: toBool(row.IsDefault),
    RequireExplicitSelection: toBool(row.RequireExplicitSelection),
    Active: toBool(row.Active === undefined ? true : row.Active),
  }));
};

const normalizeProgramSpecialtyCatalog = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => ({
    ClientId: createClientId(),
    pk_ProgramSpecialty: coerceNumber(row.pk_ProgramSpecialty, null),
    SpecialtyKey: row.SpecialtyKey || '',
    DisplayName: row.DisplayName || '',
    Level: row.Level || 'specialty',
    TrainingType: row.TrainingType || 'residency',
    ParentProgramSpecialty: coerceNumber(row.ParentProgramSpecialty, null),
    ExternalCode: row.ExternalCode || '',
    SourceSystem: row.SourceSystem || 'LOCAL',
    Active: toBool(row.Active === undefined ? true : row.Active),
  }));
};

const normalizePolicyRequirement = (rawRequirement = {}, fallbackIndex = 0) => {
  const requirementType = REQUIREMENT_TYPE_OPTIONS.includes(rawRequirement.RequirementType)
    ? rawRequirement.RequirementType
    : 'pdf';
  const templateId = requirementType === 'template' ? coerceNumber(rawRequirement.fk_LoRTemplate, null) : null;

  return {
    ClientId: createClientId(),
    pk_LoRPolicyRequirement: coerceNumber(rawRequirement.pk_LoRPolicyRequirement, null),
    RequirementType: requirementType,
    fk_LoRTemplate: templateId,
    IsRequired: toBool(rawRequirement.IsRequired === undefined ? true : rawRequirement.IsRequired),
    ConditionalRuleJson:
      rawRequirement.ConditionalRuleJson && typeof rawRequirement.ConditionalRuleJson === 'object'
        ? JSON.stringify(rawRequirement.ConditionalRuleJson)
        : rawRequirement.ConditionalRuleJson || '',
    DisplayOrder: coerceNumber(rawRequirement.DisplayOrder, fallbackIndex),
    Active: toBool(rawRequirement.Active === undefined ? true : rawRequirement.Active),
  };
};

const normalizePolicySets = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row, rowIndex) => {
    const scopeType = POLICY_SCOPE_OPTIONS.includes(row.ScopeType) ? row.ScopeType : 'season_default';
    const requirementsRaw = Array.isArray(row.requirements)
      ? row.requirements
      : Array.isArray(row.Requirements)
      ? row.Requirements
      : [];

    const normalizedRequirements = requirementsRaw.map((requirement, requirementIndex) =>
      normalizePolicyRequirement(requirement, requirementIndex),
    );

    if (normalizedRequirements.length === 0) {
      normalizedRequirements.push(normalizePolicyRequirement({ RequirementType: 'pdf' }, 0));
    }

    return {
      ClientId: createClientId(),
      pk_LoRPolicySet: coerceNumber(row.pk_LoRPolicySet, null),
      ScopeType: scopeType,
      fk_DepartmentProgramSpecialty:
        scopeType === 'season_default' ? null : coerceNumber(row.fk_DepartmentProgramSpecialty, null),
      PublishStatus: row.PublishStatus || 'draft',
      Version: coerceNumber(row.Version, 1),
      PublishedAt: row.PublishedAt || null,
      requirements: normalizedRequirements,
      sortOrder: rowIndex,
    };
  });
};

const serializeOfferings = (rows = []) => {
  return JSON.stringify(
    rows.map((row) => ({
      pk_DepartmentProgramSpecialty: row.pk_DepartmentProgramSpecialty || null,
      fk_ProgramSpecialty: row.fk_ProgramSpecialty || null,
      IsDefault: !!row.IsDefault,
      RequireExplicitSelection: !!row.RequireExplicitSelection,
      Active: !!row.Active,
    })),
  );
};

const serializeCatalog = (rows = []) => {
  return JSON.stringify(
    rows.map((row) => ({
      pk_ProgramSpecialty: row.pk_ProgramSpecialty || null,
      SpecialtyKey: `${row.SpecialtyKey || ''}`.trim().toUpperCase(),
      DisplayName: `${row.DisplayName || ''}`.trim(),
      Level: row.Level || 'specialty',
      TrainingType: row.TrainingType || 'residency',
      ParentProgramSpecialty: row.ParentProgramSpecialty || null,
      ExternalCode: row.ExternalCode || '',
      SourceSystem: row.SourceSystem || 'LOCAL',
      Active: !!row.Active,
    })),
  );
};

const serializePolicySets = (rows = []) => {
  return JSON.stringify(
    rows.map((row) => ({
      pk_LoRPolicySet: row.pk_LoRPolicySet || null,
      ScopeType: row.ScopeType,
      fk_DepartmentProgramSpecialty:
        row.ScopeType === 'season_default' ? null : row.fk_DepartmentProgramSpecialty || null,
      PublishStatus: row.PublishStatus || 'draft',
      requirements: (row.requirements || []).map((requirement) => ({
        RequirementType: requirement.RequirementType,
        fk_LoRTemplate: requirement.RequirementType === 'template' ? requirement.fk_LoRTemplate || null : null,
        IsRequired: !!requirement.IsRequired,
        ConditionalRuleJson: requirement.ConditionalRuleJson || '',
        DisplayOrder: coerceNumber(requirement.DisplayOrder, 0),
        Active: !!requirement.Active,
      })),
    })),
  );
};

const DirectApplicationSettings = ({ windowWidth }) => {
  const [isGettingSettings, setIsGettingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [baseURL, setBaseURL] = useState();
  const [editableStartDate, setEditableStartDate] = useState(null);
  const [editableEndDate, setEditableEndDate] = useState(null);
  const [activeSeason, setActiveSeason] = useState(null);

  const [isGettingLoRSettings, setIsGettingLoRSettings] = useState(false);
  const [isSavingLoRSettings, setIsSavingLoRSettings] = useState(false);
  const [loRSettings, setLoRSettings] = useState({ ...DEFAULT_LOR_SETTINGS });
  const [initialLoRSettings, setInitialLoRSettings] = useState({ ...DEFAULT_LOR_SETTINGS });
  const [loRTemplates, setLoRTemplates] = useState([]);

  const [isGettingLoRMappings, setIsGettingLoRMappings] = useState(false);
  const [isSavingLoRMappings, setIsSavingLoRMappings] = useState(false);
  const [activeMappingRankType, setActiveMappingRankType] = useState('evaluation');
  const [loRScoreMappings, setLoRScoreMappings] = useState({ ...defaultMappingsByRankType });
  const [loRScoreMappingsDirty, setLoRScoreMappingsDirty] = useState({ evaluation: false, prescreen: false });
  const [mappingSaveErrors, setMappingSaveErrors] = useState([]);
  const [weightCategories, setWeightCategories] = useState([]);
  const [availableDomains, setAvailableDomains] = useState(Object.keys(DOMAIN_LABELS));

  const [isGettingSpecialtyOfferings, setIsGettingSpecialtyOfferings] = useState(false);
  const [isSavingSpecialtyOfferings, setIsSavingSpecialtyOfferings] = useState(false);
  const [departmentProgramSpecialties, setDepartmentProgramSpecialties] = useState([]);
  const [initialDepartmentProgramSpecialties, setInitialDepartmentProgramSpecialties] = useState([]);
  const [programSpecialtyCatalog, setProgramSpecialtyCatalog] = useState([]);
  const [initialProgramSpecialtyCatalog, setInitialProgramSpecialtyCatalog] = useState([]);

  const [isGettingLoRPolicy, setIsGettingLoRPolicy] = useState(false);
  const [isSavingLoRPolicy, setIsSavingLoRPolicy] = useState(false);
  const [isPublishingLoRPolicy, setIsPublishingLoRPolicy] = useState(false);
  const [policySaveErrors, setPolicySaveErrors] = useState([]);
  const [loRPolicySets, setLoRPolicySets] = useState([]);
  const [initialLoRPolicySets, setInitialLoRPolicySets] = useState([]);
  const [loRPolicyTemplates, setLoRPolicyTemplates] = useState([]);
  const [loRPolicyOfferings, setLoRPolicyOfferings] = useState([]);
  const [selectedPolicySetId, setSelectedPolicySetId] = useState(null);
  const [isGettingPolicyImpact, setIsGettingPolicyImpact] = useState(false);
  const [policyImpactPreview, setPolicyImpactPreview] = useState(null);

  const dContext = useContext(DepartmentContext);
  const { department, season } = dContext;
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const isSuperUser = !!department?.EnableSuperUser;

  const widthToSwitchToMobile = 700;

  useEffect(() => {
    const { protocol, hostname, port } = window.location;
    const newBaseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    setBaseURL(newBaseUrl);
  }, []);

  useEffect(() => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    getSettings();
    getLoRSettings();
    getLoRMappings();
    getDepartmentProgramSpecialties();
    getLoRPolicy();
  }, [department?.pk_Department, season?.pk_Season]);

  useEffect(() => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    fetchPolicyImpactPreview(selectedPolicySetId || null);
  }, [department?.pk_Department, season?.pk_Season, selectedPolicySetId]);

  const updateActiveSeason = (newSeasonList = []) => {
    const seasonId = dContext?.season?.pk_Season;
    if (!seasonId) {
      setActiveSeason(null);
      setEditableStartDate(null);
      setEditableEndDate(null);
      return;
    }

    const newActiveSeason = newSeasonList.find((item) => item.pk_Season === seasonId);
    setActiveSeason(newActiveSeason || null);

    setEditableStartDate(newActiveSeason?.ApplicationStartDate ? new Date(newActiveSeason.ApplicationStartDate) : null);
    setEditableEndDate(newActiveSeason?.ApplicationEndDate ? new Date(newActiveSeason.ApplicationEndDate) : null);
  };

  const getSettings = () => {
    setIsGettingSettings(true);

    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic('department', { pk_Department: department.pk_Department }, formatBearerToken(token))
          .then((res) => {
            const { data = {} } = res;
            const { season: seasonList = [] } = data;
            updateActiveSeason(seasonList);
          })
          .catch(() => {
            toast.error('Failed to load direct application season settings');
          })
          .finally(() => {
            setIsGettingSettings(false);
          });
      })
      .catch((err) => {
        setIsGettingSettings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveSeason = () => {
    if (!editableStartDate || !editableEndDate) {
      return;
    }

    setIsSaving(true);

    getTokenSilently()
      .then((token) => {
        putData(
          'department/season',
          { pk_Department: department.pk_Department, pk_Season: season.pk_Season },
          {
            seasonObject: {
              ApplicationStartDate: dateFormatToDBFormat(new Date(dayjs(editableStartDate).format())),
              ApplicationEndDate: dateFormatToDBFormat(new Date(dayjs(editableEndDate).format())),
            },
          },
          formatBearerToken(token),
        )
          .then(() => {
            toast.success('Season dates saved successfully');
            getSettings();
          })
          .catch((err) => {
            console.log('saveSeason err: ', err);
            toast.error('Error saving season dates');
          })
          .finally(() => {
            setIsSaving(false);
          });
      })
      .catch((err) => {
        setIsSaving(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getLoRSettings = () => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    setIsGettingLoRSettings(true);

    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/application/letters/settings',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const settingsPayload = normalizeLoRSettings(res?.data?.settings || {});
            setLoRSettings(settingsPayload);
            setInitialLoRSettings(settingsPayload);
            setLoRTemplates(Array.isArray(res?.data?.templates) ? res.data.templates : []);
          })
          .catch((err) => {
            console.log('getLoRSettings err: ', err);
            toast.error('Failed to load LoR settings');
          })
          .finally(() => {
            setIsGettingLoRSettings(false);
          });
      })
      .catch((err) => {
        setIsGettingLoRSettings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveLoRSettings = () => {
    const minRequired = coerceNumber(loRSettings.LoRMinRequired, null);
    const maxAccepted = coerceNumber(loRSettings.LoRMaxAccepted, null);

    if (minRequired === null || maxAccepted === null || minRequired < 0 || maxAccepted < 0) {
      toast.error('LoR minimum and maximum must be non-negative numbers.');
      return;
    }

    if (minRequired > maxAccepted) {
      toast.error('LoR minimum required cannot be greater than maximum accepted.');
      return;
    }

    setIsSavingLoRSettings(true);

    getTokenSilently()
      .then((token) => {
        putData(
          'department/application/letters/settings',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          {
            LoRMinRequired: minRequired,
            LoRMaxAccepted: maxAccepted,
            LoRRequiredForReview: loRSettings.LoRRequiredForReview,
            LoRTemplatePolicyJson: {
              RequireStandardizedTemplate: loRSettings.LoRTemplatePolicyJson.RequireStandardizedTemplate,
              AllowedTemplateIds: loRSettings.LoRTemplatePolicyJson.AllowedTemplateIds,
            },
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const settingsPayload = normalizeLoRSettings(res?.data?.settings || {});
            setLoRSettings(settingsPayload);
            setInitialLoRSettings(settingsPayload);
            toast.success('LoR settings saved');
          })
          .catch((err) => {
            console.log('saveLoRSettings err: ', err);
            toast.error('Failed to save LoR settings');
          })
          .finally(() => {
            setIsSavingLoRSettings(false);
          });
      })
      .catch((err) => {
        setIsSavingLoRSettings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getLoRMappings = () => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    setIsGettingLoRMappings(true);

    getTokenSilently()
      .then(async (token) => {
        const headers = formatBearerToken(token);

        const fetchByRankType = (rankType) =>
          fetchDataAgnostic(
            'department/application/letters/score-mappings',
            {
              pk_Department: department.pk_Department,
              pk_Season: season.pk_Season,
              RankType: rankType,
            },
            headers,
          );

        Promise.all([fetchByRankType('evaluation'), fetchByRankType('prescreen')])
          .then(([evaluationRes, prescreenRes]) => {
            const evaluationData = evaluationRes?.data || {};
            const prescreenData = prescreenRes?.data || {};

            setLoRScoreMappings({
              evaluation: normalizeMappings(evaluationData.mappings || []),
              prescreen: normalizeMappings(prescreenData.mappings || []),
            });
            setLoRScoreMappingsDirty({ evaluation: false, prescreen: false });
            setMappingSaveErrors([]);

            const fetchedWeightCategories = evaluationData.weightCategories || prescreenData.weightCategories || [];
            setWeightCategories(Array.isArray(fetchedWeightCategories) ? fetchedWeightCategories : []);

            const fetchedDomains = evaluationData.availableDomains || prescreenData.availableDomains || [];
            if (Array.isArray(fetchedDomains) && fetchedDomains.length > 0) {
              setAvailableDomains(fetchedDomains);
            } else {
              setAvailableDomains(Object.keys(DOMAIN_LABELS));
            }
          })
          .catch((err) => {
            console.log('getLoRMappings err: ', err);
            toast.error('Failed to load LoR score mappings');
          })
          .finally(() => {
            setIsGettingLoRMappings(false);
          });
      })
      .catch((err) => {
        setIsGettingLoRMappings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getDepartmentProgramSpecialties = () => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    setIsGettingSpecialtyOfferings(true);

    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/application/specialties',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const normalizedOfferings = normalizeDepartmentProgramSpecialties(res?.data?.offerings || []);
            const normalizedCatalog = normalizeProgramSpecialtyCatalog(
              Array.isArray(res?.data?.catalog) ? res.data.catalog : [],
            );
            setDepartmentProgramSpecialties(normalizedOfferings);
            setInitialDepartmentProgramSpecialties(normalizedOfferings);
            setProgramSpecialtyCatalog(normalizedCatalog);
            setInitialProgramSpecialtyCatalog(normalizedCatalog);
          })
          .catch((err) => {
            console.log('getDepartmentProgramSpecialties err: ', err);
            toast.error('Failed to load specialty offerings');
          })
          .finally(() => {
            setIsGettingSpecialtyOfferings(false);
          });
      })
      .catch((err) => {
        setIsGettingSpecialtyOfferings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveDepartmentProgramSpecialties = () => {
    if (!isSuperUser) {
      toast.error('Only super users can edit specialty offerings');
      return;
    }

    if ((departmentProgramSpecialties || []).filter((row) => row.Active).length === 0) {
      toast.error('At least one active specialty offering is required');
      return;
    }

    const payloadRows = (departmentProgramSpecialties || []).map((row, rowIndex) => ({
      pk_DepartmentProgramSpecialty: row.pk_DepartmentProgramSpecialty || undefined,
      fk_ProgramSpecialty: coerceNumber(row.fk_ProgramSpecialty, null),
      IsDefault: !!row.IsDefault,
      RequireExplicitSelection: !!row.RequireExplicitSelection,
      Active: !!row.Active,
      DisplayOrder: rowIndex,
    }));

    if (payloadRows.some((row) => !row.fk_ProgramSpecialty)) {
      toast.error('Each specialty offering must select a specialty catalog entry');
      return;
    }

    const catalogPayload = (programSpecialtyCatalog || []).map((catalogRow) => ({
      pk_ProgramSpecialty: catalogRow.pk_ProgramSpecialty || undefined,
      SpecialtyKey: `${catalogRow.SpecialtyKey || ''}`.trim().toUpperCase(),
      DisplayName: `${catalogRow.DisplayName || ''}`.trim(),
      Level: catalogRow.Level || 'specialty',
      TrainingType: catalogRow.TrainingType || 'residency',
      ParentProgramSpecialty: coerceNumber(catalogRow.ParentProgramSpecialty, null),
      ExternalCode: catalogRow.ExternalCode ? `${catalogRow.ExternalCode}`.trim() : null,
      SourceSystem: catalogRow.SourceSystem || 'LOCAL',
      Active: !!catalogRow.Active,
    }));

    if (catalogPayload.some((catalogRow) => !catalogRow.SpecialtyKey || !catalogRow.DisplayName)) {
      toast.error('Each catalog row needs both SpecialtyKey and DisplayName');
      return;
    }

    const uniqueKeys = new Set(catalogPayload.map((catalogRow) => catalogRow.SpecialtyKey));
    if (uniqueKeys.size !== catalogPayload.length) {
      toast.error('Catalog SpecialtyKey values must be unique');
      return;
    }

    setIsSavingSpecialtyOfferings(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/application/specialties',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          {
            Offerings: payloadRows,
            CatalogEntries: catalogPayload,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const normalizedOfferings = normalizeDepartmentProgramSpecialties(res?.data?.offerings || []);
            const normalizedCatalog = normalizeProgramSpecialtyCatalog(
              Array.isArray(res?.data?.catalog) ? res.data.catalog : [],
            );
            setDepartmentProgramSpecialties(normalizedOfferings);
            setInitialDepartmentProgramSpecialties(normalizedOfferings);
            setProgramSpecialtyCatalog(normalizedCatalog);
            setInitialProgramSpecialtyCatalog(normalizedCatalog);
            toast.success('Specialty catalog and offerings saved');
            getLoRPolicy();
          })
          .catch((err) => {
            console.log('saveDepartmentProgramSpecialties err: ', err);
            toast.error(err?.response?.data?.message || 'Failed to save specialty offerings');
          })
          .finally(() => {
            setIsSavingSpecialtyOfferings(false);
          });
      })
      .catch((err) => {
        setIsSavingSpecialtyOfferings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getLoRPolicy = () => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    setIsGettingLoRPolicy(true);
    setPolicySaveErrors([]);

    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/application/lor-policy',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const normalizedPolicySets = normalizePolicySets(res?.data?.policySets || []);
            setLoRPolicySets(normalizedPolicySets);
            setInitialLoRPolicySets(normalizedPolicySets);
            setLoRPolicyTemplates(Array.isArray(res?.data?.templates) ? res.data.templates : []);
            setLoRPolicyOfferings(
              normalizeDepartmentProgramSpecialties(Array.isArray(res?.data?.offerings) ? res.data.offerings : []),
            );

            if (normalizedPolicySets.length > 0) {
              const publishedSet = normalizedPolicySets.find((policySet) => policySet.PublishStatus === 'published');
              const defaultSet = publishedSet || normalizedPolicySets[0];
              setSelectedPolicySetId(defaultSet.pk_LoRPolicySet || defaultSet.ClientId);
            } else {
              setSelectedPolicySetId(null);
            }
          })
          .catch((err) => {
            console.log('getLoRPolicy err: ', err);
            toast.error('Failed to load LoR policy');
          })
          .finally(() => {
            setIsGettingLoRPolicy(false);
          });
      })
      .catch((err) => {
        setIsGettingLoRPolicy(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const fetchPolicyImpactPreview = (policySetId = null) => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    const normalizedPolicySetId = coerceNumber(policySetId, null);

    setIsGettingPolicyImpact(true);
    getTokenSilently()
      .then((token) => {
        const params = {
          pk_Department: department.pk_Department,
          pk_Season: season.pk_Season,
        };
        if (normalizedPolicySetId) {
          params.pk_LoRPolicySet = normalizedPolicySetId;
        }

        fetchDataAgnostic('department/application/lor-policy/impact-preview', params, formatBearerToken(token))
          .then((res) => {
            setPolicyImpactPreview(res?.data?.impact || null);
          })
          .catch((err) => {
            console.log('fetchPolicyImpactPreview err: ', err);
            toast.error('Failed to load LoR policy impact preview');
          })
          .finally(() => {
            setIsGettingPolicyImpact(false);
          });
      })
      .catch((err) => {
        setIsGettingPolicyImpact(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveLoRPolicy = () => {
    if (!isSuperUser) {
      toast.error('Only super users can edit LoR policy');
      return;
    }

    const validationErrors = [];
    const payloadPolicySets = loRPolicySets.map((policySet) => {
      if (policySet.ScopeType !== 'season_default' && !coerceNumber(policySet.fk_DepartmentProgramSpecialty, null)) {
        validationErrors.push('Specialty/track policy sets must target a department offering');
      }

      const requirements = (policySet.requirements || [])
        .filter((requirement) => requirement.Active)
        .map((requirement, requirementIndex) => {
          if (requirement.RequirementType === 'template' && !coerceNumber(requirement.fk_LoRTemplate, null)) {
            validationErrors.push('Template requirements must select a standardized template');
          }

          return {
            pk_LoRPolicyRequirement: requirement.pk_LoRPolicyRequirement || undefined,
            RequirementType: requirement.RequirementType,
            fk_LoRTemplate:
              requirement.RequirementType === 'template' ? coerceNumber(requirement.fk_LoRTemplate, null) : null,
            IsRequired: !!requirement.IsRequired,
            ConditionalRuleJson: requirement.ConditionalRuleJson ? requirement.ConditionalRuleJson : null,
            DisplayOrder: coerceNumber(requirement.DisplayOrder, requirementIndex),
            Active: !!requirement.Active,
          };
        });

      if (requirements.length === 0) {
        validationErrors.push('Every policy set needs at least one active requirement');
      }

      return {
        pk_LoRPolicySet: policySet.pk_LoRPolicySet || undefined,
        ScopeType: policySet.ScopeType,
        fk_DepartmentProgramSpecialty:
          policySet.ScopeType === 'season_default' ? null : coerceNumber(policySet.fk_DepartmentProgramSpecialty, null),
        PublishStatus: policySet.PublishStatus || 'draft',
        Requirements: requirements,
      };
    });

    if (validationErrors.length > 0) {
      const deduped = [...new Set(validationErrors)];
      setPolicySaveErrors(deduped);
      toast.error(deduped[0]);
      return;
    }

    setPolicySaveErrors([]);
    setIsSavingLoRPolicy(true);

    getTokenSilently()
      .then((token) => {
        putData(
          'department/application/lor-policy',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          {
            PolicySets: payloadPolicySets,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const normalizedPolicySets = normalizePolicySets(res?.data?.policySets || []);
            setLoRPolicySets(normalizedPolicySets);
            setInitialLoRPolicySets(normalizedPolicySets);
            setLoRPolicyTemplates(Array.isArray(res?.data?.templates) ? res.data.templates : []);
            setLoRPolicyOfferings(
              normalizeDepartmentProgramSpecialties(Array.isArray(res?.data?.offerings) ? res.data.offerings : []),
            );
            if (normalizedPolicySets.length > 0 && !selectedPolicySetId) {
              setSelectedPolicySetId(normalizedPolicySets[0].pk_LoRPolicySet || normalizedPolicySets[0].ClientId);
            }
            toast.success('LoR policy saved');
            fetchPolicyImpactPreview(selectedPolicySetId || normalizedPolicySets[0]?.pk_LoRPolicySet || null);
          })
          .catch((err) => {
            console.log('saveLoRPolicy err: ', err);
            const validationMessages = extractApiErrorMessages(err, 'Failed to save LoR policy');
            setPolicySaveErrors(validationMessages);
            toast.error(validationMessages[0]);
          })
          .finally(() => {
            setIsSavingLoRPolicy(false);
          });
      })
      .catch((err) => {
        setIsSavingLoRPolicy(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const publishPolicySet = (policySetId) => {
    if (!isSuperUser) {
      toast.error('Only super users can publish LoR policy');
      return;
    }

    if (!policySetId) {
      return;
    }

    setIsPublishingLoRPolicy(true);
    setPolicySaveErrors([]);

    getTokenSilently()
      .then((token) => {
        postDataAgnostic(
          'department/application/lor-policy/publish',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          {
            pk_LoRPolicySet: policySetId,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const normalizedPolicySets = normalizePolicySets(res?.data?.policySets || []);
            setLoRPolicySets(normalizedPolicySets);
            setInitialLoRPolicySets(normalizedPolicySets);
            setSelectedPolicySetId(policySetId);
            toast.success('LoR policy published');
            fetchPolicyImpactPreview(policySetId);
          })
          .catch((err) => {
            console.log('publishPolicySet err: ', err);
            toast.error(err?.response?.data?.message || 'Failed to publish LoR policy');
          })
          .finally(() => {
            setIsPublishingLoRPolicy(false);
          });
      })
      .catch((err) => {
        setIsPublishingLoRPolicy(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveLoRScoreMappings = () => {
    const activeRows = loRScoreMappings[activeMappingRankType] || [];
    setMappingSaveErrors([]);

    if (
      activeRows.some(
        (row) => !row.SourceDomainKey || row.TargetWeightCategory === null || row.TargetWeightCategory === undefined,
      )
    ) {
      const validationMessage = 'Each LoR mapping row must include a source domain and target weight category.';
      setMappingSaveErrors([validationMessage]);
      toast.error(validationMessage);
      return;
    }

    const payloadRows = activeRows.map((row) => ({
      SourceDomainKey: row.SourceDomainKey,
      TargetWeightCategory: Number(row.TargetWeightCategory),
      ContributionType: row.ContributionType || 'normalized',
      Multiplier: coerceNumber(row.Multiplier, 1),
      Enabled: !!row.Enabled,
    }));

    setIsSavingLoRMappings(true);

    getTokenSilently()
      .then((token) => {
        putData(
          'department/application/letters/score-mappings',
          {
            pk_Department: department.pk_Department,
            pk_Season: season.pk_Season,
          },
          {
            RankType: activeMappingRankType,
            Mappings: payloadRows,
          },
          formatBearerToken(token),
        )
          .then((res) => {
            const updatedRows = normalizeMappings(res?.data?.mappings || []);
            setLoRScoreMappings((prev) => ({
              ...prev,
              [activeMappingRankType]: updatedRows,
            }));
            setLoRScoreMappingsDirty((prev) => ({
              ...prev,
              [activeMappingRankType]: false,
            }));
            setMappingSaveErrors([]);
            toast.success('LoR score mappings saved');
          })
          .catch((err) => {
            console.log('saveLoRScoreMappings err: ', err);
            const validationMessages = extractApiErrorMessages(err, 'Failed to save LoR score mappings');
            setMappingSaveErrors(validationMessages);
            toast.error(validationMessages[0] || 'Failed to save LoR score mappings');
          })
          .finally(() => {
            setIsSavingLoRMappings(false);
          });
      })
      .catch((err) => {
        setIsSavingLoRMappings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const hasDateChange = () => {
    const formattedEditableEndDate = editableEndDate ? dayjs(editableEndDate).format() : null;
    const formattedEditableStartDate = editableStartDate ? dayjs(editableStartDate).format() : null;

    const formattedStartDate = activeSeason ? dayjs(activeSeason.ApplicationStartDate).format() : null;
    const formattedEndDate = activeSeason ? dayjs(activeSeason.ApplicationEndDate).format() : null;

    if (activeSeason) {
      return (
        editableStartDate &&
        editableEndDate &&
        (formattedEditableEndDate !== formattedEndDate || formattedEditableStartDate !== formattedStartDate)
      );
    }

    return false;
  };

  const hasLoRSettingsChange = useMemo(() => {
    return JSON.stringify(loRSettings) !== JSON.stringify(initialLoRSettings);
  }, [loRSettings, initialLoRSettings]);

  const hasSpecialtyOfferingChange = useMemo(() => {
    return serializeOfferings(departmentProgramSpecialties) !== serializeOfferings(initialDepartmentProgramSpecialties);
  }, [departmentProgramSpecialties, initialDepartmentProgramSpecialties]);

  const hasSpecialtyCatalogChange = useMemo(() => {
    return serializeCatalog(programSpecialtyCatalog) !== serializeCatalog(initialProgramSpecialtyCatalog);
  }, [programSpecialtyCatalog, initialProgramSpecialtyCatalog]);

  const hasLoRPolicyChange = useMemo(() => {
    return serializePolicySets(loRPolicySets) !== serializePolicySets(initialLoRPolicySets);
  }, [loRPolicySets, initialLoRPolicySets]);

  const activePolicySet = useMemo(() => {
    if (!selectedPolicySetId) {
      return loRPolicySets[0] || null;
    }

    return (
      loRPolicySets.find(
        (policySet) => policySet.pk_LoRPolicySet === selectedPolicySetId || policySet.ClientId === selectedPolicySetId,
      ) || null
    );
  }, [loRPolicySets, selectedPolicySetId]);

  const catalogById = useMemo(() => {
    const output = {};
    (programSpecialtyCatalog || []).forEach((entry) => {
      output[entry.pk_ProgramSpecialty] = entry;
    });
    return output;
  }, [programSpecialtyCatalog]);

  const activeMappings = useMemo(() => {
    return loRScoreMappings[activeMappingRankType] || [];
  }, [loRScoreMappings, activeMappingRankType]);

  const weightCategoryById = useMemo(() => {
    const output = {};
    (weightCategories || []).forEach((category) => {
      output[category.pk_EvaluationQuestionWeight] = category;
    });
    return output;
  }, [weightCategories]);

  const mappingSummary = useMemo(() => {
    const enabledMappings = activeMappings.filter((row) => row.Enabled);
    let projectedContribution = 0;

    enabledMappings.forEach((row) => {
      const weightCategory = weightCategoryById[row.TargetWeightCategory];
      if (!weightCategory) {
        return;
      }

      const baseWeight =
        activeMappingRankType === 'prescreen'
          ? coerceNumber(weightCategory.PrescreenWeight, 0)
          : coerceNumber(weightCategory.EvaluationWeight, 0);

      projectedContribution += baseWeight * coerceNumber(row.Multiplier, 1);
    });

    return {
      enabledCount: enabledMappings.length,
      projectedContribution,
    };
  }, [activeMappings, activeMappingRankType, weightCategoryById]);

  const updateLoRSettingsValue = (fieldKey, value) => {
    setLoRSettings((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const updateTemplatePolicy = (fieldKey, value) => {
    setLoRSettings((prev) => ({
      ...prev,
      LoRTemplatePolicyJson: {
        ...(prev.LoRTemplatePolicyJson || DEFAULT_TEMPLATE_POLICY),
        [fieldKey]: value,
      },
    }));
  };

  const handleTemplateSelectionChange = (event) => {
    const selectedValues = Array.from(event.target.selectedOptions || [])
      .map((option) => Number(option.value))
      .filter((value) => !Number.isNaN(value));

    updateTemplatePolicy('AllowedTemplateIds', selectedValues);
  };

  const upsertProgramSpecialtyCatalogRow = (rowIndex, fieldKey, value) => {
    setProgramSpecialtyCatalog((prev) => {
      const rows = [...prev];
      rows[rowIndex] = {
        ...rows[rowIndex],
        [fieldKey]: value,
      };
      return rows;
    });
  };

  const addProgramSpecialtyCatalogRow = () => {
    setProgramSpecialtyCatalog((prev) => [
      ...prev,
      {
        ClientId: createClientId(),
        pk_ProgramSpecialty: null,
        SpecialtyKey: '',
        DisplayName: '',
        Level: 'specialty',
        TrainingType: 'residency',
        ParentProgramSpecialty: null,
        ExternalCode: '',
        SourceSystem: 'LOCAL',
        Active: true,
      },
    ]);
  };

  const removeProgramSpecialtyCatalogRow = (rowIndex) => {
    setProgramSpecialtyCatalog((prev) => {
      const rows = [...prev];
      rows.splice(rowIndex, 1);
      return rows;
    });
  };

  const upsertSpecialtyOfferingRow = (rowIndex, fieldKey, value) => {
    setDepartmentProgramSpecialties((prev) => {
      const rows = [...prev];
      const nextRow = {
        ...rows[rowIndex],
        [fieldKey]: value,
      };

      if (fieldKey === 'fk_ProgramSpecialty') {
        const selectedCatalog = catalogById[value] || null;
        if (selectedCatalog) {
          nextRow.DisplayName = selectedCatalog.DisplayName || '';
          nextRow.SpecialtyKey = selectedCatalog.SpecialtyKey || '';
          nextRow.Level = selectedCatalog.Level || '';
          nextRow.TrainingType = selectedCatalog.TrainingType || '';
        }
      }

      rows[rowIndex] = nextRow;
      return rows;
    });
  };

  const setDefaultSpecialtyOffering = (targetIndex) => {
    setDepartmentProgramSpecialties((prev) => {
      return prev.map((row, rowIndex) => ({
        ...row,
        IsDefault: rowIndex === targetIndex,
      }));
    });
  };

  const addSpecialtyOffering = () => {
    const fallbackCatalogEntry =
      (programSpecialtyCatalog || []).find((catalogEntry) => catalogEntry.Active) || programSpecialtyCatalog[0] || null;
    setDepartmentProgramSpecialties((prev) => [
      ...prev,
      {
        ClientId: createClientId(),
        pk_DepartmentProgramSpecialty: null,
        fk_ProgramSpecialty: fallbackCatalogEntry?.pk_ProgramSpecialty || null,
        DisplayName: fallbackCatalogEntry?.DisplayName || '',
        SpecialtyKey: fallbackCatalogEntry?.SpecialtyKey || '',
        Level: fallbackCatalogEntry?.Level || '',
        TrainingType: fallbackCatalogEntry?.TrainingType || '',
        IsDefault: prev.length === 0,
        RequireExplicitSelection: false,
        Active: true,
      },
    ]);
  };

  const removeSpecialtyOffering = (rowIndex) => {
    setDepartmentProgramSpecialties((prev) => {
      const rows = [...prev];
      rows.splice(rowIndex, 1);
      if (!rows.some((row) => row.IsDefault) && rows.length > 0) {
        rows[0].IsDefault = true;
      }
      return rows;
    });
  };

  const addLoRPolicySet = () => {
    const fallbackOffering = (loRPolicyOfferings || []).find((offering) => offering.Active) || loRPolicyOfferings[0];
    const nextPolicySet = {
      ClientId: createClientId(),
      pk_LoRPolicySet: null,
      ScopeType: loRPolicySets.length === 0 ? 'season_default' : 'specialty',
      fk_DepartmentProgramSpecialty: fallbackOffering?.pk_DepartmentProgramSpecialty || null,
      PublishStatus: 'draft',
      Version: 1,
      PublishedAt: null,
      requirements: [normalizePolicyRequirement({ RequirementType: 'pdf' }, 0)],
    };

    if (nextPolicySet.ScopeType === 'season_default') {
      nextPolicySet.fk_DepartmentProgramSpecialty = null;
    }

    setLoRPolicySets((prev) => [...prev, nextPolicySet]);
    setSelectedPolicySetId(nextPolicySet.ClientId);
  };

  const upsertLoRPolicySet = (policySetId, fieldKey, value) => {
    setLoRPolicySets((prev) =>
      prev.map((policySet) => {
        if (policySet.pk_LoRPolicySet !== policySetId && policySet.ClientId !== policySetId) {
          return policySet;
        }

        const updatedPolicySet = {
          ...policySet,
          [fieldKey]: value,
        };

        if (fieldKey === 'ScopeType' && value === 'season_default') {
          updatedPolicySet.fk_DepartmentProgramSpecialty = null;
        }

        return updatedPolicySet;
      }),
    );
  };

  const addLoRPolicyRequirement = (policySetId) => {
    setLoRPolicySets((prev) =>
      prev.map((policySet) => {
        if (policySet.pk_LoRPolicySet !== policySetId && policySet.ClientId !== policySetId) {
          return policySet;
        }

        const existingRequirements = policySet.requirements || [];
        return {
          ...policySet,
          requirements: [
            ...existingRequirements,
            normalizePolicyRequirement({ RequirementType: 'pdf' }, existingRequirements.length),
          ],
        };
      }),
    );
  };

  const upsertLoRPolicyRequirement = (policySetId, requirementIndex, fieldKey, value) => {
    setLoRPolicySets((prev) =>
      prev.map((policySet) => {
        if (policySet.pk_LoRPolicySet !== policySetId && policySet.ClientId !== policySetId) {
          return policySet;
        }

        const nextRequirements = [...(policySet.requirements || [])];
        nextRequirements[requirementIndex] = {
          ...nextRequirements[requirementIndex],
          [fieldKey]: value,
        };

        if (fieldKey === 'RequirementType' && value !== 'template') {
          nextRequirements[requirementIndex].fk_LoRTemplate = null;
        }

        return {
          ...policySet,
          requirements: nextRequirements,
        };
      }),
    );
  };

  const removeLoRPolicyRequirement = (policySetId, requirementIndex) => {
    setLoRPolicySets((prev) =>
      prev.map((policySet) => {
        if (policySet.pk_LoRPolicySet !== policySetId && policySet.ClientId !== policySetId) {
          return policySet;
        }

        const nextRequirements = [...(policySet.requirements || [])];
        nextRequirements.splice(requirementIndex, 1);

        return {
          ...policySet,
          requirements:
            nextRequirements.length > 0
              ? nextRequirements
              : [normalizePolicyRequirement({ RequirementType: 'pdf' }, 0)],
        };
      }),
    );
  };

  const upsertMappingRow = (rowIndex, fieldKey, value) => {
    setMappingSaveErrors([]);
    setLoRScoreMappings((prev) => {
      const rows = [...(prev[activeMappingRankType] || [])];
      rows[rowIndex] = {
        ...rows[rowIndex],
        [fieldKey]: value,
      };

      return {
        ...prev,
        [activeMappingRankType]: rows,
      };
    });

    setLoRScoreMappingsDirty((prev) => ({
      ...prev,
      [activeMappingRankType]: true,
    }));
  };

  const addMappingRow = () => {
    const fallbackDomain = availableDomains[0] || 'overall_rank';
    setMappingSaveErrors([]);

    setLoRScoreMappings((prev) => {
      const rows = [...(prev[activeMappingRankType] || [])];
      rows.push({
        ClientId: createClientId(),
        SourceDomainKey: fallbackDomain,
        TargetWeightCategory: null,
        ContributionType: 'normalized',
        Multiplier: 1,
        Enabled: true,
      });

      return {
        ...prev,
        [activeMappingRankType]: rows,
      };
    });

    setLoRScoreMappingsDirty((prev) => ({
      ...prev,
      [activeMappingRankType]: true,
    }));
  };

  const removeMappingRow = (rowIndex) => {
    setMappingSaveErrors([]);
    setLoRScoreMappings((prev) => {
      const rows = [...(prev[activeMappingRankType] || [])];
      rows.splice(rowIndex, 1);

      return {
        ...prev,
        [activeMappingRankType]: rows,
      };
    });

    setLoRScoreMappingsDirty((prev) => ({
      ...prev,
      [activeMappingRankType]: true,
    }));
  };

  const switchMappingRankType = (rankType) => {
    setActiveMappingRankType(rankType);
    setMappingSaveErrors([]);
  };

  const renderMappingRows = () => {
    if (activeMappings.length === 0) {
      return (
        <tr>
          <td colSpan={6} style={{ fontSize: 13, textAlign: 'center' }}>
            No LoR mappings configured for {activeMappingRankType}.
          </td>
        </tr>
      );
    }

    return activeMappings.map((row, rowIndex) => {
      return (
        <tr key={row.ClientId || `${activeMappingRankType}_${rowIndex}`}>
          <td>
            <Input
              type="select"
              value={row.SourceDomainKey}
              onChange={(event) => upsertMappingRow(rowIndex, 'SourceDomainKey', event.target.value)}
              disabled={isSavingLoRMappings}
            >
              {(availableDomains || []).map((domainKey) => (
                <option key={domainKey} value={domainKey}>
                  {DOMAIN_LABELS[domainKey] || domainKey}
                </option>
              ))}
            </Input>
          </td>
          <td>
            <Input
              type="select"
              value={row.TargetWeightCategory || ''}
              onChange={(event) =>
                upsertMappingRow(
                  rowIndex,
                  'TargetWeightCategory',
                  event.target.value ? Number(event.target.value) : null,
                )
              }
              disabled={isSavingLoRMappings}
            >
              <option value="">Select weight category</option>
              {(weightCategories || []).map((category) => {
                const weight =
                  activeMappingRankType === 'prescreen' ? category.PrescreenWeight : category.EvaluationWeight;

                return (
                  <option key={category.pk_EvaluationQuestionWeight} value={category.pk_EvaluationQuestionWeight}>
                    {`${category.CategoryName} (${Number(weight || 0).toFixed(1)}%)`}
                  </option>
                );
              })}
            </Input>
          </td>
          <td>
            <Input
              type="select"
              value={row.ContributionType || 'normalized'}
              onChange={(event) => upsertMappingRow(rowIndex, 'ContributionType', event.target.value)}
              disabled={isSavingLoRMappings}
            >
              <option value="normalized">Normalized</option>
              <option value="raw">Raw</option>
            </Input>
          </td>
          <td>
            <Input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={row.Multiplier}
              onChange={(event) => upsertMappingRow(rowIndex, 'Multiplier', coerceNumber(event.target.value, 1))}
              disabled={isSavingLoRMappings}
            />
          </td>
          <td>
            <Input
              type="checkbox"
              checked={!!row.Enabled}
              onChange={(event) => upsertMappingRow(rowIndex, 'Enabled', event.target.checked)}
              disabled={isSavingLoRMappings}
            />
          </td>
          <td>
            <Button
              size="sm"
              color="danger"
              outline
              onClick={() => removeMappingRow(rowIndex)}
              disabled={isSavingLoRMappings}
            >
              Remove
            </Button>
          </td>
        </tr>
      );
    });
  };

  return (
    <div style={style.simpleRow}>
      <div style={{ ...style.simpleColumn, width: '100%' }}>
        <GenerateApplicationURLCode
          windowWidth={windowWidth}
          widthToSwitchToMobile={widthToSwitchToMobile}
          baseURL={baseURL}
        />

        <br />

        <Card>
          <CardHeader>
            <div style={{ ...style.simpleRow, alignItems: 'center', fontWeight: 'bold' }}>
              Application Window for {activeSeason ? activeSeason.SeasonName : ''}
            </div>
          </CardHeader>
          <CardBody>
            {(() => {
              const isMobile = typeof window !== 'undefined' && window.innerWidth < widthToSwitchToMobile;

              return (
                <div
                  style={
                    isMobile ? { display: 'flex', flexDirection: 'column', gap: 10, width: '100%' } : { width: '100%' }
                  }
                >
                  <div style={{ width: '100%' }}>
                    <RezRateBanner
                      message={activeSeason && !activeSeason.Active ? 'This is an inactive season!' : null}
                      backgroundColor={activeSeason && !activeSeason.Active ? '#f0ad4e' : null}
                    />
                  </div>

                  <div
                    style={
                      isMobile
                        ? { width: '100%' }
                        : {
                            display: 'flex',
                            flexDirection: 'row',
                            paddingRight: '10%',
                            paddingLeft: '10%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 10,
                          }
                    }
                  >
                    <div
                      style={
                        isMobile
                          ? { width: '100%' }
                          : { ...style.simpleColumn, width: '50%', paddingLeft: 30, paddingRight: 30 }
                      }
                    >
                      <div style={style.simpleRow}>Start Accepting Applications on</div>
                      <div style={{ ...style.simpleColumn, width: '100%' }}>
                        <DatePicker
                          placeholder="Select a start date"
                          value={editableStartDate}
                          disabled={isGettingSettings || isSaving}
                          onChange={(event) => {
                            setEditableStartDate(event);
                          }}
                          valueFormat={{ month: 'short', year: 'numeric' }}
                          valueEditFormat={{ dateStyle: 'long' }}
                          valueDisplayFormat={{ dateStyle: 'long' }}
                        />
                      </div>
                    </div>

                    <div
                      style={
                        isMobile
                          ? { width: '100%' }
                          : { ...style.simpleColumn, width: '50%', paddingLeft: 30, paddingRight: 30 }
                      }
                    >
                      <div style={style.simpleRow}>End Accepting Applications on</div>
                      <div style={style.simpleRow}>
                        <div style={{ ...style.simpleColumn, width: '100%' }}>
                          <DatePicker
                            placeholder="Select an end date"
                            value={editableEndDate}
                            disabled={isGettingSettings || isSaving}
                            onChange={(event) => {
                              setEditableEndDate(event);
                            }}
                            valueFormat={{ month: 'short', year: 'numeric' }}
                            valueEditFormat={{ dateStyle: 'long' }}
                            valueDisplayFormat={{ dateStyle: 'long' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                      size="sm"
                      disabled={!hasDateChange() || isSaving}
                      style={{ width: 150 }}
                      color="success"
                      onClick={saveSeason}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardBody>
        </Card>

        <br />

        {showAdvancedDirectApplicationSettings && isSuperUser ? (
          <>
        <Card>
          <CardHeader>
            <div style={{ ...style.simpleRow, justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontWeight: 'bold' }}>Specialty / Track Offerings</div>
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={!isSuperUser || isSavingSpecialtyOfferings || isGettingSpecialtyOfferings}
                onClick={addSpecialtyOffering}
              >
                Add Offering
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
              {isSuperUser
                ? 'Super users can manage department-season specialty/track offerings.'
                : 'Read-only: only super users can edit offerings.'}
            </div>

            {isGettingSpecialtyOfferings ? (
              <div style={{ fontSize: 13 }}>Loading specialty offerings...</div>
            ) : (
              <>
                <div
                  style={{ ...style.simpleRow, justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}
                >
                  <div style={{ fontWeight: 600 }}>Specialty Catalog</div>
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    disabled={!isSuperUser || isSavingSpecialtyOfferings}
                    onClick={addProgramSpecialtyCatalogRow}
                  >
                    Add Catalog Entry
                  </Button>
                </div>

                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Specialty Key</th>
                      <th>Display Name</th>
                      <th>Level</th>
                      <th>Training Type</th>
                      <th>Source</th>
                      <th>Active</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {programSpecialtyCatalog.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ fontSize: 13, textAlign: 'center' }}>
                          No specialty catalog entries configured.
                        </td>
                      </tr>
                    ) : (
                      programSpecialtyCatalog.map((catalogRow, catalogIndex) => (
                        <tr key={catalogRow.ClientId || catalogRow.pk_ProgramSpecialty || catalogIndex}>
                          <td style={{ minWidth: 170 }}>
                            <Input
                              value={catalogRow.SpecialtyKey || ''}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(
                                  catalogIndex,
                                  'SpecialtyKey',
                                  event.target.value.toUpperCase().replace(/\s+/g, '_'),
                                )
                              }
                            />
                          </td>
                          <td style={{ minWidth: 200 }}>
                            <Input
                              value={catalogRow.DisplayName || ''}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(catalogIndex, 'DisplayName', event.target.value)
                              }
                            />
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <Input
                              type="select"
                              value={catalogRow.Level || 'specialty'}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(catalogIndex, 'Level', event.target.value)
                              }
                            >
                              <option value="specialty">Specialty</option>
                              <option value="subspecialty">Subspecialty</option>
                              <option value="track">Track</option>
                            </Input>
                          </td>
                          <td style={{ minWidth: 150 }}>
                            <Input
                              type="select"
                              value={catalogRow.TrainingType || 'residency'}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(catalogIndex, 'TrainingType', event.target.value)
                              }
                            >
                              <option value="residency">Residency</option>
                              <option value="fellowship">Fellowship</option>
                            </Input>
                          </td>
                          <td style={{ minWidth: 130 }}>
                            <Input
                              value={catalogRow.SourceSystem || 'LOCAL'}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(
                                  catalogIndex,
                                  'SourceSystem',
                                  event.target.value.toUpperCase(),
                                )
                              }
                            />
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <Input
                              type="checkbox"
                              checked={!!catalogRow.Active}
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onChange={(event) =>
                                upsertProgramSpecialtyCatalogRow(catalogIndex, 'Active', event.target.checked)
                              }
                            />
                          </td>
                          <td style={{ width: 90 }}>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              disabled={!isSuperUser || isSavingSpecialtyOfferings}
                              onClick={() => removeProgramSpecialtyCatalogRow(catalogIndex)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
                <div style={{ marginTop: -4, marginBottom: 10, fontSize: 12, color: '#666' }}>
                  Save catalog changes before assigning newly added entries to offerings.
                </div>

                <div style={{ fontWeight: 600, marginBottom: 8 }}>Department Offering Mapping</div>

                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Catalog Entry</th>
                      <th>Default</th>
                      <th>Require Selection</th>
                      <th>Active</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentProgramSpecialties.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ fontSize: 13, textAlign: 'center' }}>
                          No offerings configured yet.
                        </td>
                      </tr>
                    ) : (
                      departmentProgramSpecialties.map((offering, offeringIndex) => {
                        const selectedCatalogEntry = catalogById[offering.fk_ProgramSpecialty];

                        return (
                          <tr key={offering.ClientId || offering.pk_DepartmentProgramSpecialty || offeringIndex}>
                            <td style={{ minWidth: 280 }}>
                              <Input
                                type="select"
                                disabled={!isSuperUser || isSavingSpecialtyOfferings}
                                value={offering.fk_ProgramSpecialty || ''}
                                onChange={(event) =>
                                  upsertSpecialtyOfferingRow(
                                    offeringIndex,
                                    'fk_ProgramSpecialty',
                                    event.target.value ? Number(event.target.value) : null,
                                  )
                                }
                              >
                                <option value="">Select specialty/track</option>
                                {programSpecialtyCatalog
                                  .filter((catalogRow) => catalogRow.Active)
                                  .map((catalogRow) => (
                                    <option key={catalogRow.pk_ProgramSpecialty} value={catalogRow.pk_ProgramSpecialty}>
                                      {`${catalogRow.DisplayName} (${catalogRow.Level ||
                                        'specialty'} / ${catalogRow.TrainingType || 'residency'})`}
                                    </option>
                                  ))}
                              </Input>
                              <div style={{ marginTop: 4, fontSize: 11, color: '#666' }}>
                                {selectedCatalogEntry?.SpecialtyKey || offering.SpecialtyKey || '--'}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Input
                                type="checkbox"
                                checked={!!offering.IsDefault}
                                disabled={!isSuperUser || isSavingSpecialtyOfferings}
                                onChange={(event) => {
                                  if (event.target.checked) {
                                    setDefaultSpecialtyOffering(offeringIndex);
                                  } else {
                                    upsertSpecialtyOfferingRow(offeringIndex, 'IsDefault', false);
                                  }
                                }}
                              />
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Input
                                type="checkbox"
                                checked={!!offering.RequireExplicitSelection}
                                disabled={!isSuperUser || isSavingSpecialtyOfferings}
                                onChange={(event) =>
                                  upsertSpecialtyOfferingRow(
                                    offeringIndex,
                                    'RequireExplicitSelection',
                                    event.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Input
                                type="checkbox"
                                checked={!!offering.Active}
                                disabled={!isSuperUser || isSavingSpecialtyOfferings}
                                onChange={(event) =>
                                  upsertSpecialtyOfferingRow(offeringIndex, 'Active', event.target.checked)
                                }
                              />
                            </td>
                            <td style={{ width: 90 }}>
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                disabled={!isSuperUser || isSavingSpecialtyOfferings}
                                onClick={() => removeSpecialtyOffering(offeringIndex)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>

                <Button
                  size="sm"
                  color="success"
                  disabled={
                    !isSuperUser ||
                    (!hasSpecialtyOfferingChange && !hasSpecialtyCatalogChange) ||
                    isSavingSpecialtyOfferings
                  }
                  onClick={saveDepartmentProgramSpecialties}
                >
                  {isSavingSpecialtyOfferings ? 'Saving...' : 'Save Catalog + Offerings'}
                </Button>
              </>
            )}
          </CardBody>
        </Card>

        <br />

        <Card>
          <CardHeader>
            <div style={{ ...style.simpleRow, justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontWeight: 'bold' }}>LoR Policy Builder</div>
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={!isSuperUser || isSavingLoRPolicy || isGettingLoRPolicy}
                onClick={addLoRPolicySet}
              >
                Add Policy Set
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
              {isSuperUser
                ? 'Edit draft policy sets, then publish to enforce requirements.'
                : 'Read-only: only super users can edit or publish policy sets.'}
            </div>

            {isGettingLoRPolicy ? (
              <div style={{ fontSize: 13 }}>Loading LoR policy...</div>
            ) : (
              <>
                {policySaveErrors.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: '#fff4e5',
                      border: '1px solid #ffd29d',
                      borderRadius: 4,
                      padding: 10,
                      marginBottom: 10,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Policy validation issues:</div>
                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                      {policySaveErrors.slice(0, 5).map((errorItem, index) => (
                        <li key={`${errorItem}_${index}`}>{errorItem}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <FormGroup style={{ maxWidth: 420 }}>
                  <Label>Active Policy Set</Label>
                  <Input
                    type="select"
                    value={selectedPolicySetId || ''}
                    onChange={(event) =>
                      setSelectedPolicySetId(
                        event.target.value ? Number(event.target.value) || event.target.value : null,
                      )
                    }
                  >
                    {loRPolicySets.map((policySet, policySetIndex) => (
                      <option
                        key={policySet.pk_LoRPolicySet || policySet.ClientId}
                        value={policySet.pk_LoRPolicySet || policySet.ClientId}
                      >
                        {`${POLICY_SCOPE_LABELS[policySet.ScopeType] ||
                          policySet.ScopeType} #${policySet.pk_LoRPolicySet ||
                          policySetIndex + 1} (${policySet.PublishStatus || 'draft'})`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>

                {activePolicySet ? (
                  <>
                    <div style={{ ...style.simpleRow, gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div style={{ minWidth: 200 }}>
                        <Label>Scope</Label>
                        <Input
                          type="select"
                          value={activePolicySet.ScopeType}
                          disabled={!isSuperUser || isSavingLoRPolicy}
                          onChange={(event) =>
                            upsertLoRPolicySet(
                              activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                              'ScopeType',
                              event.target.value,
                            )
                          }
                        >
                          {POLICY_SCOPE_OPTIONS.map((scopeOption) => (
                            <option key={scopeOption} value={scopeOption}>
                              {POLICY_SCOPE_LABELS[scopeOption] || scopeOption}
                            </option>
                          ))}
                        </Input>
                      </div>

                      {activePolicySet.ScopeType !== 'season_default' ? (
                        <div style={{ minWidth: 280 }}>
                          <Label>Offering</Label>
                          <Input
                            type="select"
                            value={activePolicySet.fk_DepartmentProgramSpecialty || ''}
                            disabled={!isSuperUser || isSavingLoRPolicy}
                            onChange={(event) =>
                              upsertLoRPolicySet(
                                activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                'fk_DepartmentProgramSpecialty',
                                event.target.value ? Number(event.target.value) : null,
                              )
                            }
                          >
                            <option value="">Select offering</option>
                            {loRPolicyOfferings
                              .filter((offering) => offering.Active)
                              .map((offering) => (
                                <option
                                  key={offering.pk_DepartmentProgramSpecialty}
                                  value={offering.pk_DepartmentProgramSpecialty}
                                >
                                  {offering.DisplayName || offering.SpecialtyKey}
                                </option>
                              ))}
                          </Input>
                        </div>
                      ) : null}

                      <div style={{ minWidth: 200 }}>
                        <Label>Publish Status</Label>
                        <Input type="text" disabled value={activePolicySet.PublishStatus || 'draft'} />
                      </div>

                      <div style={{ minWidth: 160 }}>
                        <Label>Version</Label>
                        <Input type="text" disabled value={activePolicySet.Version || 1} />
                      </div>
                    </div>

                    {activePolicySet.PublishedAt ? (
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                        Published: {dayjs(activePolicySet.PublishedAt).format('MMM D, YYYY h:mm A')}
                      </div>
                    ) : null}

                    <Table size="sm" bordered responsive>
                      <thead>
                        <tr>
                          <th>Requirement Type</th>
                          <th>Template</th>
                          <th>Required</th>
                          <th>Conditional Rule JSON</th>
                          <th>Active</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(activePolicySet.requirements || []).map((requirement, requirementIndex) => (
                          <tr key={requirement.ClientId || requirement.pk_LoRPolicyRequirement || requirementIndex}>
                            <td style={{ minWidth: 180 }}>
                              <Input
                                type="select"
                                disabled={!isSuperUser || isSavingLoRPolicy}
                                value={requirement.RequirementType}
                                onChange={(event) =>
                                  upsertLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                    'RequirementType',
                                    event.target.value,
                                  )
                                }
                              >
                                {REQUIREMENT_TYPE_OPTIONS.map((requirementType) => (
                                  <option key={requirementType} value={requirementType}>
                                    {REQUIREMENT_TYPE_LABELS[requirementType] || requirementType}
                                  </option>
                                ))}
                              </Input>
                            </td>
                            <td style={{ minWidth: 260 }}>
                              <Input
                                type="select"
                                disabled={
                                  !isSuperUser || isSavingLoRPolicy || requirement.RequirementType !== 'template'
                                }
                                value={requirement.fk_LoRTemplate || ''}
                                onChange={(event) =>
                                  upsertLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                    'fk_LoRTemplate',
                                    event.target.value ? Number(event.target.value) : null,
                                  )
                                }
                              >
                                <option value="">Select template</option>
                                {loRPolicyTemplates.map((template) => (
                                  <option key={template.pk_LoRTemplate} value={template.pk_LoRTemplate}>
                                    {`${template.Name} (${template.Version})`}
                                  </option>
                                ))}
                              </Input>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Input
                                type="checkbox"
                                checked={!!requirement.IsRequired}
                                disabled={!isSuperUser || isSavingLoRPolicy}
                                onChange={(event) =>
                                  upsertLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                    'IsRequired',
                                    event.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td style={{ minWidth: 280 }}>
                              <Input
                                type="textarea"
                                rows={2}
                                disabled={!isSuperUser || isSavingLoRPolicy}
                                value={requirement.ConditionalRuleJson || ''}
                                onChange={(event) =>
                                  upsertLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                    'ConditionalRuleJson',
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Input
                                type="checkbox"
                                checked={!!requirement.Active}
                                disabled={!isSuperUser || isSavingLoRPolicy}
                                onChange={(event) =>
                                  upsertLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                    'Active',
                                    event.target.checked,
                                  )
                                }
                              />
                            </td>
                            <td style={{ width: 90 }}>
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                disabled={!isSuperUser || isSavingLoRPolicy}
                                onClick={() =>
                                  removeLoRPolicyRequirement(
                                    activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId,
                                    requirementIndex,
                                  )
                                }
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    <div style={{ ...style.simpleRow, gap: 10, flexWrap: 'wrap' }}>
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        disabled={!isSuperUser || isSavingLoRPolicy}
                        onClick={() =>
                          addLoRPolicyRequirement(activePolicySet.pk_LoRPolicySet || activePolicySet.ClientId)
                        }
                      >
                        Add Requirement
                      </Button>
                      <Button
                        size="sm"
                        color="success"
                        disabled={!isSuperUser || !hasLoRPolicyChange || isSavingLoRPolicy}
                        onClick={saveLoRPolicy}
                      >
                        {isSavingLoRPolicy ? 'Saving...' : 'Save Policy Draft'}
                      </Button>
                      <Button
                        size="sm"
                        color="primary"
                        disabled={
                          !isSuperUser || isPublishingLoRPolicy || !coerceNumber(activePolicySet.pk_LoRPolicySet, null)
                        }
                        onClick={() => publishPolicySet(activePolicySet.pk_LoRPolicySet)}
                      >
                        {isPublishingLoRPolicy ? 'Publishing...' : 'Publish'}
                      </Button>
                      <Button
                        size="sm"
                        color="info"
                        outline
                        disabled={isGettingPolicyImpact}
                        onClick={() =>
                          fetchPolicyImpactPreview(coerceNumber(activePolicySet.pk_LoRPolicySet, null) || null)
                        }
                      >
                        Refresh Impact Preview
                      </Button>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#666' }}>No policy set configured yet.</div>
                )}

                <div style={{ marginTop: 12, fontSize: 13 }}>
                  <b>Impact Preview</b>
                  {isGettingPolicyImpact ? (
                    <div style={{ marginTop: 6 }}>Loading impact preview...</div>
                  ) : (
                    <div style={{ marginTop: 6 }}>
                      <div>{`Total requests in scope: ${policyImpactPreview?.totalRequests || 0}`}</div>
                      <div>{`Submitted requests: ${policyImpactPreview?.submittedRequests || 0}`}</div>
                      <div>{`Pending requests: ${policyImpactPreview?.pendingRequests || 0}`}</div>
                      {policyImpactPreview?.policySet ? (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          {`Previewing policy set #${policyImpactPreview.policySet.pk_LoRPolicySet} (${policyImpactPreview.policySet.ScopeType})`}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <br />

        <Card>
          <CardHeader>
            <div style={{ fontWeight: 'bold' }}>Letters of Recommendation Requirements</div>
          </CardHeader>
          <CardBody>
            {isGettingLoRSettings ? (
              <div style={{ fontSize: 13 }}>Loading LoR settings...</div>
            ) : (
              <>
                <div style={{ ...style.simpleRow, gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 180 }}>
                    <Label for="lor-min-required">Minimum Required</Label>
                    <Input
                      id="lor-min-required"
                      type="number"
                      min="0"
                      max="10"
                      value={loRSettings.LoRMinRequired}
                      onChange={(event) =>
                        updateLoRSettingsValue('LoRMinRequired', coerceNumber(event.target.value, 0))
                      }
                      disabled={isSavingLoRSettings}
                    />
                  </div>

                  <div style={{ minWidth: 180 }}>
                    <Label for="lor-max-accepted">Maximum Accepted</Label>
                    <Input
                      id="lor-max-accepted"
                      type="number"
                      min="0"
                      max="20"
                      value={loRSettings.LoRMaxAccepted}
                      onChange={(event) =>
                        updateLoRSettingsValue('LoRMaxAccepted', coerceNumber(event.target.value, 0))
                      }
                      disabled={isSavingLoRSettings}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={loRSettings.LoRRequiredForReview}
                      disabled={isSavingLoRSettings}
                      onChange={(event) => updateLoRSettingsValue('LoRRequiredForReview', event.target.checked)}
                    />{' '}
                    Require minimum LoRs before coordinator review
                  </Label>
                </div>

                <div style={{ marginTop: 10 }}>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={toBool(loRSettings.LoRTemplatePolicyJson.RequireStandardizedTemplate)}
                      disabled={isSavingLoRSettings}
                      onChange={(event) => updateTemplatePolicy('RequireStandardizedTemplate', event.target.checked)}
                    />{' '}
                    Require standardized template selection for LoR requests
                  </Label>
                </div>

                <div style={{ marginTop: 14, maxWidth: 520 }}>
                  <Label for="lor-allowed-templates">Allowed Templates (multi-select)</Label>
                  <Input
                    id="lor-allowed-templates"
                    type="select"
                    multiple
                    value={loRSettings.LoRTemplatePolicyJson.AllowedTemplateIds.map((templateId) => `${templateId}`)}
                    onChange={handleTemplateSelectionChange}
                    disabled={isSavingLoRSettings}
                    style={{ minHeight: 140 }}
                  >
                    {loRTemplates.map((template) => (
                      <option key={template.pk_LoRTemplate} value={template.pk_LoRTemplate}>
                        {`${template.Name} (${template.Version})`}
                      </option>
                    ))}
                  </Input>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                    Leave empty to allow any active template.
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Button
                    size="sm"
                    color="success"
                    onClick={saveLoRSettings}
                    disabled={!hasLoRSettingsChange || isSavingLoRSettings}
                  >
                    {isSavingLoRSettings ? 'Saving...' : 'Save LoR Requirements'}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <br />

        <Card>
          <CardHeader>
            <div
              style={{
                ...style.simpleRow,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ fontWeight: 'bold' }}>LoR Score Mappings</div>
              <div>
                <Button
                  size="sm"
                  color={activeMappingRankType === 'evaluation' ? 'primary' : 'secondary'}
                  outline={activeMappingRankType !== 'evaluation'}
                  onClick={() => switchMappingRankType('evaluation')}
                  disabled={isSavingLoRMappings}
                  style={{ marginRight: 8 }}
                >
                  Evaluation
                </Button>
                <Button
                  size="sm"
                  color={activeMappingRankType === 'prescreen' ? 'primary' : 'secondary'}
                  outline={activeMappingRankType !== 'prescreen'}
                  onClick={() => switchMappingRankType('prescreen')}
                  disabled={isSavingLoRMappings}
                >
                  Prescreen
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {isGettingLoRMappings ? (
              <div style={{ fontSize: 13 }}>Loading LoR score mappings...</div>
            ) : (
              <>
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  {`Enabled mappings: ${
                    mappingSummary.enabledCount
                  } | Projected contribution: ${mappingSummary.projectedContribution.toFixed(1)}%`}
                </div>

                {mappingSaveErrors.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: '#fff4e5',
                      border: '1px solid #ffd29d',
                      borderRadius: 4,
                      padding: 10,
                      marginBottom: 10,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Please fix the following mapping issues:</div>
                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                      {mappingSaveErrors.slice(0, 5).map((errorItem, index) => (
                        <li key={`${errorItem}_${index}`}>{errorItem}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>LoR Domain</th>
                      <th>Weight Category</th>
                      <th>Source</th>
                      <th>Multiplier</th>
                      <th>Enabled</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>{renderMappingRows()}</tbody>
                </Table>

                <div style={{ ...style.simpleRow, gap: 10, flexWrap: 'wrap' }}>
                  <Button size="sm" color="secondary" outline onClick={addMappingRow} disabled={isSavingLoRMappings}>
                    Add Mapping
                  </Button>
                  <Button
                    size="sm"
                    color="success"
                    onClick={saveLoRScoreMappings}
                    disabled={!loRScoreMappingsDirty[activeMappingRankType] || isSavingLoRMappings}
                  >
                    {isSavingLoRMappings ? 'Saving...' : `Save ${activeMappingRankType} Mappings`}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DirectApplicationSettings;
